import { AccountCircle, AdminPanelSettings, Home, Logout } from '@mui/icons-material';
import InsightsIcon from '@mui/icons-material/Insights';
import {
    AppBar,
    Box,
    Button,
    Chip,
    Container,
    createTheme,
    CssBaseline,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    ThemeProvider,
    Toolbar,
    Typography
} from '@mui/material';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useNavigate } from 'react-router-dom';
import { logout } from '../../store/slices/authSlice';
import { RootState } from '../../types';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#3f51b5',
    },
    secondary: {
      main: '#f50057',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
    },
  },
  typography: {
    fontFamily: [
      '"Noto Sans JP"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          minHeight: '100vh',
        },
      },
    },
  },
});

interface AppLayoutProps {
  children?: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
    handleMenuClose();
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{
        flexGrow: 1,
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        backgroundAttachment: 'fixed',
      }}>
        <AppBar position="static" sx={{
          background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)',
        }}>
          <Toolbar>
            <InsightsIcon sx={{ mr: 2 }} />
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              社内ナレッジ共有システム
            </Typography>

            {isAuthenticated && user && (
              <>
                <Button
                  color="inherit"
                  startIcon={<Home />}
                  onClick={() => navigate('/')}
                  sx={{ mr: 1 }}
                >
                  ホーム
                </Button>

                {user.role === 'admin' && (
                  <Button
                    color="inherit"
                    startIcon={<AdminPanelSettings />}
                    onClick={() => navigate('/admin')}
                    sx={{ mr: 2 }}
                  >
                    管理者
                  </Button>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    {user.name}
                  </Typography>
                  <Chip
                    label={user.role === 'admin' ? '管理者' : 'ユーザー'}
                    size="small"
                    color={user.role === 'admin' ? 'secondary' : 'default'}
                    sx={{ mr: 1 }}
                  />
                </Box>

                <IconButton
                  size="large"
                  edge="end"
                  aria-label="account menu"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenuOpen}
                  color="inherit"
                >
                  <AccountCircle />
                </IconButton>

                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  <MenuItem disabled>
                    <Box>
                      <Typography variant="subtitle2">{user.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => handleNavigation('/')}>
                    <Home sx={{ mr: 1 }} />
                    ホーム
                  </MenuItem>
                  {user.role === 'admin' && (
                    <MenuItem onClick={() => handleNavigation('/admin')}>
                      <AdminPanelSettings sx={{ mr: 1 }} />
                      管理者ページ
                    </MenuItem>
                  )}
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <Logout sx={{ mr: 1 }} />
                    ログアウト
                  </MenuItem>
                </Menu>
              </>
            )}
          </Toolbar>
        </AppBar>
        <Container maxWidth="xl" sx={{
          mt: 4,
          mb: 4,
          '& .MuiPaper-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
            borderRadius: '10px',
            border: '1px solid rgba(255, 255, 255, 0.18)',
          }
        }}>
          {children || <Outlet />}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default AppLayout;
